"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { haptics } from "@/lib/haptics";
import {
  BAR_WEIGHTS,
  PLATE_SETS,
  computeTotal,
  decomposeTargetClosest,
  defaultBarWeight,
} from "@/lib/plate-math";
import { kgToLb, lbToKg, type WeightUnit } from "@/lib/stats";

/**
 * NumericKeypadPlateCalc — plate calculator panel (v9.218).
 *
 * Sits to the left of the keypad in the three-panel carousel:
 *   plateCalc ← keypad → calculator
 *
 * ## v9.218 changes (post-second-review)
 *
 *   - BAR cell moved INTO the grid as the 8th slot. Sub-header
 *     no longer carries the bar chip — it's just the helper hint
 *     + diff readout now. Simplifies the layout: 7 plates + 1
 *     bar cell fills the 8-slot grid exactly.
 *   - Inactive plate cells muted further (bg-surface-2/40 +
 *     text-fg-muted/60) so the brand-fill active cells dominate
 *     the visual hierarchy.
 *   - Long-press on a plate cell resets that plate to 0
 *     (~500 ms hold, light haptic confirmation). Combines with
 *     the existing tap-to-increment and corner-minus-to-decrement
 *     into a complete +1 / −1 / reset gesture vocabulary per cell.
 *   - TARGET in the header is now a button that returns to the
 *     keypad — the canonical "edit target" path. The header's
 *     `<` back arrow does the same; making TARGET tappable too
 *     means lifters who think "the target is wrong, fix it"
 *     can act on the affordance they're looking at.
 *
 * ## Interactions
 *
 *   Plate cell tap         → +1 (light haptic)
 *   Plate cell corner `−`  → −1 (light haptic; only when count > 0)
 *   Plate cell long-press  → reset to 0 (~500 ms, light haptic)
 *   BAR cell tap           → cycle bar weight
 *   `<` or TARGET tap      → back to keypad (no value change)
 *   `×` header tap         → reset all plates (bar stays)
 *   `✓ Apply N KG`         → write TOTAL back to keypad
 */

export type NumericKeypadPlateCalcProps = {
  initialValue: string;
  unit: WeightUnit;
  onApply: (total: number) => void;
  /** Programmatic dismiss — TARGET tap fires this to bounce back
   *  to the keypad for value editing. Distinct from the cycle
   *  chevrons because "tap to edit value" is a specific intent
   *  (always go to keypad) rather than directional navigation. */
  onClose: () => void;
  /** Back to the numpad panel.
   *
   *  KEYPAD_MODERN — replaces the v9.236 pair of cycle callbacks
   *  (`onCyclePrev` / `onCycleNext` plus their panel-name labels). That
   *  machinery was built for a three-panel carousel; v9.265 retired the
   *  calculator panel and left a two-panel set, at which point "previous"
   *  and "next" were the same place and the header carried two arrows that
   *  did the same thing. One named button beats two mystery chevrons. If a
   *  third panel ever arrives, the loop can come back with it. */
  onBackToKeypad: () => void;
};

// v9.246 — Variant A (visual barbell + colour-coded plate cells)
// promoted to default after admin-preview comparison. Variants B
// (math expression) and C (corner colour dots) retired. Per-variant
// branching removed; what shipped reflects the PlatesA layout the
// user picked, plus all the iterative polish:
//   - Barbell SVG above the plate grid, with stepped widths and
//     real powerlifting plate colours
//   - HTML labels above the SVG (per-side / bar / per-side) at
//     constant pixel size regardless of SVG zoom
//   - Active plate cells fill with the plate's real colour
//   - BAR cell fills with the SVG bar's grey when bar > 0
//   - X reset restores the target's auto-decomposition (was: blank)
//   - Width-based plate cap (220 SVG units) prevents extreme
//     loads from squashing the visual

const LONG_PRESS_MS = 500;

// v9.245 — Standard powerlifting plate colours, shared between
// Variant C (cell-dot mnemonic) and Variant A (SVG plate fill).
// Module-scope so both consumers reference the same map; no per-
// render allocation.
//
// KG — IPF / IWF calibrated plate colours:
//   25  = red       (the "red family" — large calibrated plate)
//   20  = blue
//   15  = yellow
//   10  = green
//   5   = white
//   2.5 = red       (small change plate, matches 25 kg colour
//                    family; SIZE differentiates them visually)
//   1.25 = dark grey (NO official standard — not an IPF/IWF
//                    competition plate. Common in UK/Euro
//                    commercial gyms as the smallest fractional;
//                    typically chrome or black at the rack. Dark
//                    grey reads as "small generic plate" without
//                    claiming a colour standard that doesn't
//                    exist.)
//
// LB — USAPL / IWF bumper-plate colour convention (LB plates
// map to the nearest KG colour family):
//   45 = blue   (matches 20 kg)
//   35 = yellow (matches 15 kg)
//   25 = green  (matches 10 kg)
//   10 = white  (matches 5 kg)
//   5  = red    (matches 2.5 kg red change family)
//   2.5  = dark grey (no convention — generic small plate)
//   1.25 = dark grey (no convention — generic micro plate)
//
// v9.245.3 fixes: KG 2.5 was light grey (now red per IPF
// standard). LB was offset by one position in the colour cycle
// (45 was red, should be blue); rotated the whole LB ramp + added
// the 1.25 LB entry that was missing.
/** Exported so the feature tour can draw plate chips from the SAME source
 *  as the real calculator. It hand-rolled its own three-colour row before,
 *  which is exactly how a mock ends up showing a product that no longer
 *  exists. Denominations come from PLATE_SETS for the same reason. */
export const PLATE_COLOURS_KG: Record<number, string> = {
  25: "#dc2626",
  20: "#2563eb",
  15: "#eab308",
  10: "#16a34a",
  5: "#ffffff",
  2.5: "#dc2626",
  1.25: "#4b5563",
};
export const PLATE_COLOURS_LB: Record<number, string> = {
  45: "#2563eb",
  35: "#eab308",
  25: "#16a34a",
  10: "#ffffff",
  5: "#dc2626",
  2.5: "#4b5563",
  1.25: "#4b5563",
};

export function NumericKeypadPlateCalc({
  initialValue,
  unit,
  onApply,
  onClose,
  onBackToKeypad,
}: NumericKeypadPlateCalcProps) {
  const denoms = PLATE_SETS[unit];
  const initialTarget = useMemo(() => {
    const parsed = Number(initialValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [initialValue]);

  const [bar, setBar] = useState<number>(() => defaultBarWeight(unit));
  // v9.256 — display-unit toggle (PlateCalc only; doesn't touch the
  // workout's session unit). The plate inventory + math stay in
  // `unit` (so plates remain in the user's gym increments, e.g. KG
  // plates if the lifter is in the UK) but the TOTAL readout can
  // show the converted value in the OTHER unit. Useful for the
  // periodisation-percentage flow Ste Baker described: think in
  // lb percentages (more granular for advanced lifters), load
  // plates in kg.
  //
  // Defaults to the workout's unit so the first paint matches the
  // session — flipping is opt-in. Local state only; resets on
  // panel close. No persistence (flip preference is per-session
  // — beta testers can let us know if they want it sticky).
  const [displayUnit, setDisplayUnit] = useState<WeightUnit>(unit);

  // v9.261 — local-target override for the inline edit overlay
  // (TARGET tap / % button). When set, plate-calc renders this
  // value as the effective target instead of `initialTarget`
  // (which comes from the keypad's `value` prop). Lets the lifter
  // iterate on target inside plate-calc — typing a new value or
  // applying a percentage — without bouncing through the keypad
  // panel. The Apply button at the bottom writes the CURRENT
  // TOTAL back to the host on commit, so localTarget is purely a
  // staging concept inside this panel.
  //
  // Resets when initialTarget (the prop-derived target) changes —
  // i.e. the user committed Apply or re-opened the panel with a
  // fresh keypad value.
  const [localTarget, setLocalTarget] = useState<number | null>(null);
  const effectiveTarget = localTarget ?? initialTarget;
  useEffect(() => {
    setLocalTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget]);

  // Inline edit-target overlay state. `overlayMode`:
  //   - "edit"    → typed value becomes the new target directly
  //   - "percent" → typed value is treated as a percent of the
  //                 CURRENT effectiveTarget (i.e. % of the target
  //                 the lifter is staring at right now). Killer
  //                 BarCalc feature for periodisation work: enter
  //                 80 → instantly loads 80% of the target weight.
  //   - null      → overlay hidden
  //
  // overlayValue is the raw typed string so the input can render
  // mid-typing (e.g. "12" before the user adds ".5"). Parsed on
  // Apply.
  const [overlayMode, setOverlayMode] = useState<"edit" | "percent" | null>(
    null,
  );
  const [overlayValue, setOverlayValue] = useState("");

  // v9.265.5 — per-mode value stash so switching the inline TOTAL ↔
  // PERCENT toggle without entering data is NON-DESTRUCTIVE. Beta
  // feedback: "when a value is present (ie 100 KG) and user moves
  // to percent and back total, without entering data, the value
  // resets to 0 KG". Pre-fix `setOverlayValue("")` on every mode
  // switch wiped whatever the lifter had typed in the leaving mode.
  // Now we stash it under the leaving mode's slot + restore from
  // the entering mode's slot.
  //
  // Refs (not state) because the stash mutation has to be
  // synchronous within the same event handler that switches mode
  // — React's batched state updates would let the swap-then-
  // restore see stale values.
  const stashedEditValueRef = useRef("");
  const stashedPercentValueRef = useRef("");

  function openOverlay(mode: "edit" | "percent") {
    haptics.light();
    setOverlayMode(mode);
    // v9.262 — always seed empty. With the in-overlay digit pad,
    // pre-seeding the existing target meant the lifter had to
    // backspace through the old value before typing a new one —
    // friction. The readout shows the existing target as a dim
    // placeholder so the lifter still sees what they're
    // replacing, and Cancel returns without changes if they
    // change their mind.
    setOverlayValue("");
    // v9.265.5 — clear stashes on overlay open so a previous
    // overlay session's typed values don't bleed into this one.
    stashedEditValueRef.current = "";
    stashedPercentValueRef.current = "";
  }
  function closeOverlay() {
    setOverlayMode(null);
    setOverlayValue("");
    // Clear stashes on close too (belt-and-braces).
    stashedEditValueRef.current = "";
    stashedPercentValueRef.current = "";
  }
  function applyOverlay() {
    haptics.medium();
    const parsed = Number(overlayValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      closeOverlay();
      return;
    }
    // Compute the new target in workout unit (math + plate
    // inventory operate in workout unit; displayUnit is just a
    // display preference).
    //
    // - PERCENT mode: typed value is unitless; multiply against
    //   effectiveTarget (already in workout unit). Result stays
    //   in workout unit.
    // - EDIT mode: typed value is in displayUnit. Convert back
    //   to workout unit (a no-op when displayUnit === unit;
    //   otherwise lbToKg or kgToLb depending on the pair).
    let newTarget: number;
    if (overlayMode === "percent") {
      newTarget = (effectiveTarget * parsed) / 100;
    } else if (displayUnit === unit) {
      newTarget = parsed;
    } else if (displayUnit === "lb") {
      // typed in lb, workout is kg
      newTarget = lbToKg(parsed);
    } else {
      // typed in kg, workout is lb
      newTarget = kgToLb(parsed);
    }
    setLocalTarget(newTarget);
    setCounts(decomposeTargetClosest(newTarget, bar, unit).counts);
    closeOverlay();
  }
  function toggleDisplayUnit() {
    haptics.light();
    // v9.264 — when toggling display unit while the inline overlay
    // is open in TOTAL ("edit") mode AND a value has been typed,
    // convert the typed digits from the old unit to the new one so
    // the lifter sees the equivalent value rather than the same
    // number with a relabeled unit. Pre-fix typing "100" with KG
    // showing then toggling to LB displayed "100 LB" (which on
    // Apply got converted as if the lifter had meant 100 lb,
    // committing ~45 kg — surprising). Now the toggle in TOTAL
    // mode displays "220.46 LB" (the actual equivalent), and Apply
    // commits the correct workout-unit weight.
    //
    // PERCENT mode unaffected — the typed value is unitless there;
    // the toggle only flips the {base} label next to it (which
    // ALREADY converts via convertForDisplay).
    const nextDisplayUnit: WeightUnit = displayUnit === "kg" ? "lb" : "kg";
    if (overlayMode === "edit" && overlayValue) {
      const parsed = Number(overlayValue);
      if (Number.isFinite(parsed) && parsed > 0) {
        const converted =
          nextDisplayUnit === "kg"
            ? lbToKg(parsed) // typed was lb, now showing kg
            : kgToLb(parsed); // typed was kg, now showing lb
        // Round to 2 dp for clean display; strip trailing zeros so
        // integers don't show as "100.00".
        setOverlayValue(
          Number.isInteger(converted)
            ? converted.toString()
            : converted.toFixed(2).replace(/\.?0+$/, ""),
        );
      }
    }
    setDisplayUnit(nextDisplayUnit);
  }
  function convertForDisplay(valueInWorkoutUnit: number): number {
    if (displayUnit === unit) return valueInWorkoutUnit;
    return displayUnit === "lb"
      ? kgToLb(valueInWorkoutUnit)
      : lbToKg(valueInWorkoutUnit);
  }

  const [counts, setCounts] = useState<number[]>(() => {
    if (initialTarget <= 0) return denoms.map(() => 0);
    return decomposeTargetClosest(initialTarget, bar, unit).counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    // Re-solve from `effectiveTarget` (the edited target when the lifter typed
    // a new value / applied a percent in the inline overlay), NOT `initialTarget`
    // (the prop-derived original). Decomposing from initialTarget here silently
    // discarded an in-panel edit on a bar switch: edit 100 → 140, tap the bar
    // chip, and the plates re-solved for 100 while TARGET still read 140, so
    // Apply wrote ~100. deps stay [bar] so this fires only on a bar change; the
    // current effectiveTarget is read from the render closure.
    if (effectiveTarget <= 0) return;
    const decomposed = decomposeTargetClosest(effectiveTarget, bar, unit);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bar cycle is an external state change
    setCounts(decomposed.counts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bar]);

  const total = computeTotal(counts, bar, unit);
  // v9.261 — diff/hasTarget read from `effectiveTarget` so the
  // inline overlay's localTarget overrides flow through to the
  // target-vs-total comparison + the TARGET button label.
  const targetDiff =
    effectiveTarget > 0
      ? Math.round((total - effectiveTarget) * 100) / 100
      : 0;
  const hasTarget = effectiveTarget > 0;
  const totalIsEmpty = total === 0;
  // v9.260 — `xButtonInactive`, `allZero`, `targetCounts`,
  // `matchesTargetLayout`, and `resetAllCounts` removed alongside
  // the X reset-button itself. The plates can still be cleared by
  // long-pressing each plate cell, and the auto-decomposed
  // suggestion still lands automatically when the user types a
  // new target value (via the bar/initialTarget useEffect below).

  // Long-press detection — per-cell timer ref. Started on
  // pointerdown; fires `resetPlate` after LONG_PRESS_MS unless
  // pointerup / pointercancel clears it first. The pointerup path
  // also handles the +1 tap so we don't fire both.
  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);

  function incrementPlate(idx: number) {
    haptics.light();
    setCounts((prev) => {
      const next = [...prev];
      next[idx] = (next[idx] ?? 0) + 1;
      return next;
    });
  }

  function decrementPlate(idx: number) {
    haptics.light();
    setCounts((prev) => {
      const next = [...prev];
      next[idx] = Math.max(0, (next[idx] ?? 0) - 1);
      return next;
    });
  }

  function resetPlate(idx: number) {
    haptics.medium();
    setCounts((prev) => {
      const next = [...prev];
      next[idx] = 0;
      return next;
    });
  }

  function handlePlatePointerDown(idx: number) {
    longPressFiredRef.current = false;
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      resetPlate(idx);
      longPressTimerRef.current = null;
    }, LONG_PRESS_MS);
  }

  function handlePlatePointerUp(idx: number, e: ReactPointerEvent<HTMLButtonElement>) {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // Long-press already fired → don't ALSO increment. The reset
    // ran in the timer callback above; nothing more to do.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      e.preventDefault();
      return;
    }
    incrementPlate(idx);
  }

  function handlePlatePointerCancel() {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressFiredRef.current = false;
  }

  // Cleanup timer on unmount so a quick close-during-long-press
  // doesn't fire reset on an unmounted component.
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current != null) {
        window.clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // v9.257 — handleBarCycle removed alongside the BAR cell that
  // called it. Bar weight is now selected by tap on the inline
  // option stack inside BarbellVisualisation; that calls
  // `handleSelectBar` below which sets the discrete value
  // directly (no cycle).
  function handleSelectBar(next: number) {
    haptics.light();
    setBar(next);
  }

  function handleApply() {
    haptics.medium();
    onApply(total);
  }

  // v9.256 — `unitLabel` is the WORKOUT unit (plate inventory math).
  // `displayUnitLabel` is the user-facing unit on the TOTAL +
  // TARGET readouts (may differ if the user toggled). Apply, diff,
  // aria-labels on plate cells, and bar-cycle aria all use the
  // workout unit since that's what's persisted.
  const unitLabel = unit.toUpperCase();
  const displayUnitLabel = displayUnit.toUpperCase();
  const formatWeight = (n: number) =>
    Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, "");
  const totalDisplay = formatWeight(convertForDisplay(total));
  const targetDisplay = formatWeight(convertForDisplay(effectiveTarget));

  const perSidePlates = denoms
    .map((denom, idx) => ({ denom, count: counts[idx] ?? 0 }))
    .filter((p) => p.count > 0);

  // v9.245 — plate colours sourced from module-scope PLATE_COLOURS
  // table (consumed by the active-cell fill AND the SVG plate fill
  // so the barbell visualisation paints plates in their real-world
  // powerlifting-plate colours).
  const plateColours = unit === "kg" ? PLATE_COLOURS_KG : PLATE_COLOURS_LB;

  // v9.245.4 — plate-cell active-state styling. When a plate is
  // active (count > 0), the cell fills with the plate's real
  // colour; text contrast adapts per plate (dark text on light
  // backgrounds — white + yellow — light text on the rest).
  // Falls back to brand-orange when the denom isn't in the colour
  // map (defensive — shouldn't happen with current PLATE_SETS but
  // keeps the visual safe if a custom plate denomination is
  // added later).
  function plateActiveStyle(colour: string | undefined): CSSProperties {
    if (!colour) {
      return {
        backgroundColor: "var(--color-brand)",
        color: "var(--color-on-brand)",
      };
    }
    const isLightPlate = colour === "#ffffff" || colour === "#eab308";
    return {
      backgroundColor: colour,
      // Dark ink on the pale plates (white/yellow), theme-agnostic — was
      // var(--color-bg) which flipped to near-white (invisible) in light.
      color: isLightPlate ? "var(--color-on-brand)" : "#ffffff",
      // The white plate (#ffffff) vanishes on the light-theme surface
      // (bg-surface is white too) — give it a hairline edge. border-box
      // keeps the cell size unchanged; harmless on dark.
      ...(colour === "#ffffff"
        ? { border: "1px solid var(--color-border)" }
        : null),
    };
  }

  return (
    // v9.237 — `flex-1 flex flex-col` fills the parent panel's
    // shared min-h-[340px] container. PlateCalc's natural height
    // (~326) is the shortest of the three panels — without this
    // fill+mt-auto trick the panel would float its Apply button
    // 14px above the bottom edge while the keypad/calc anchor
    // their commit buttons flush to the floor. See the matching
    // v9.237 notes on the parent panel + calculator components.
    <div
      className="relative flex-1 flex flex-col bg-surface"
      role="dialog"
      aria-label="Plate calculator"
    >
      {/* v9.261 → v9.262 → v9.263 — inline edit-target overlay.
          v9.262 added an in-panel digit pad (vs OS keyboard).
          v9.263 restructures to fill the panel vertically + adds:
            • TOTAL / % mode toggle inside the overlay so the
              lifter can switch interpretation mid-flow rather
              than backing out + tapping a different entry
              affordance
            • KG/LB unit toggle in the header, synced with the
              parent's `displayUnit` state — flipping here also
              flips the rest of the calc when the overlay closes
              (single source of truth). In TOTAL mode the typed
              value is in displayUnit and gets converted back to
              workout unit on Apply; in PERCENT mode the typed
              value is unitless so the toggle only changes the
              header's base label.
            • flex-col + justify-between layout so Cancel/Apply
              anchor to the panel bottom rather than floating mid-
              card. Visual rhythm matches the host keypad's
              "readout / digit grid / commit bar" stack.
          Apply commits to localTarget + re-decomposes plates;
          Cancel closes without applying. */}
      {overlayMode !== null && (
        <div
          className="absolute inset-0 z-10 bg-surface/95 backdrop-blur-sm flex flex-col px-3 py-3"
          role="dialog"
          aria-label={
            overlayMode === "percent"
              ? "Percentage of target"
              : "Edit target weight"
          }
        >
          {/* v9.265.3 — header row simplified: just the context
              label. KG/LB toggle moved INTO the readout below to
              match the keypad's merged-layout pattern + beta
              feedback: "the KG/LB values should be next to the
              value in the readout". */}
          <div className="flex items-center gap-3 mb-2">
            <p className="text-fg-dim text-xs uppercase tracking-wider font-medium truncate">
              {overlayMode === "percent" ? (
                <>
                  % of{" "}
                  <span className="text-fg-muted">
                    {targetDisplay} {displayUnitLabel}
                  </span>
                </>
              ) : (
                <>New target weight</>
              )}
            </p>
          </div>

          {/* Readout — non-editable display of the typed value.
              v9.265.3 — KG/LB toggle now lives INSIDE the readout
              in EDIT mode, sitting inline with the value (matches
              the plate-calc panel TOTAL header style). PERCENT
              mode keeps the static "%" suffix since the value is
              unitless. Empty state shows a dim placeholder
              ("80" for percent, "100" for edit) hinting at typical
              entry.
              v9.265.4 — toggle now ABSOLUTELY positioned at the
              right edge so it doesn't shift as the value's width
              changes. Pre-fix the value + toggle were both flex
              children with `gap-2`, so typing "45.36" (wider)
              pushed the toggle ~30px further right than "100"
              (narrower) — beta feedback: "when a value is entered
              and the toggle is switched, the centre text moves
              location. Can we pin the KG/LB toggle in place?"
              Now the value remains horizontally centered in the
              readout while the toggle stays anchored at `right-3`. */}
          <div
            className="w-full h-14 px-4 rounded-xl bg-surface-2 border border-border flex items-center justify-center relative mb-3"
            aria-live="polite"
            aria-atomic="true"
          >
            <span
              className={
                "text-3xl font-semibold tabular-nums tracking-tight " +
                (overlayValue ? "text-fg" : "text-fg-dim/40")
              }
            >
              {overlayValue || (overlayMode === "percent" ? "80" : "100")}
            </span>
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {overlayMode === "percent" ? (
                <span className="text-fg-dim text-lg leading-none font-medium">
                  %
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDisplayUnit();
                  }}
                  aria-label={`Display unit: ${displayUnitLabel}. Tap to switch to ${displayUnit === "kg" ? "LB" : "KG"}.`}
                  title={`Tap to switch to ${displayUnit === "kg" ? "LB" : "KG"}`}
                  className="inline-flex items-baseline gap-0.5 hover:bg-surface-3 active:bg-surface-3 rounded px-1.5 py-0.5 transition-colors"
                >
                  <span
                    className={
                      "inline-block min-w-[1.5rem] text-center tracking-wider uppercase font-medium " +
                      (displayUnit === "kg"
                        ? "text-brand text-sm"
                        : "text-fg-dim text-[10px]")
                    }
                  >
                    KG
                  </span>
                  <span
                    className={
                      "inline-block min-w-[1.5rem] text-center tracking-wider uppercase font-medium " +
                      (displayUnit === "lb"
                        ? "text-brand text-sm"
                        : "text-fg-dim text-[10px]")
                    }
                  >
                    LB
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Mode toggle — TOTAL vs % (PERCENT). Single
              SegmentedPicker-style row.
              v9.265.5 — switching modes now STASHES the leaving
              mode's typed value + RESTORES the entering mode's
              previously-stashed value (refs declared above). The
              two modes have distinct value semantics (TOTAL = a
              weight, PERCENT = a percentage), so they each get
              their own slot. Round-trip without entry is
              non-destructive — beta feedback wanted "100 KG →
              PERCENT → TOTAL" to land back at "100 KG" rather
              than wiping the typed value. */}
          {/* KEYPAD_MODERN — same segmented control as the numpad's Total /
              Percent and its KG/LB switch: raised chip, brand text, grey
              track. Three toggles across two panels used to be three
              different-looking things. */}
          <div className="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg bg-surface-3/60 mb-3">
            {(["edit", "percent"] as const).map((m) => {
              const active = overlayMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    if (active) return;
                    haptics.light();
                    // Stash CURRENT value under the LEAVING mode's
                    // slot before flipping.
                    if (overlayMode === "edit") {
                      stashedEditValueRef.current = overlayValue;
                    } else if (overlayMode === "percent") {
                      stashedPercentValueRef.current = overlayValue;
                    }
                    // Restore from the ENTERING mode's slot.
                    setOverlayValue(
                      m === "edit"
                        ? stashedEditValueRef.current
                        : stashedPercentValueRef.current,
                    );
                    setOverlayMode(m);
                  }}
                  aria-pressed={active}
                  // v9.264 — inner radius kept one step tighter than the
                  // track's so the selected chip nests inside the curve
                  // instead of fighting it.
                  className={
                    "h-8 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors " +
                    (active
                      ? "bg-surface text-brand shadow-sm"
                      : "text-fg-muted hover:text-fg")
                  }
                >
                  {m === "edit" ? "Total" : "Percent"}
                </button>
              );
            })}
          </div>

          {/* Digit pad — 3 cols × 4 rows. Mirrors the host
              numeric-keypad's layout. flex-1 so it grows to fill
              the available middle space; the commit bar below
              anchors to the bottom via mt-auto in the wrapper.
              The decimal key is suppressed in percent mode
              (whole numbers cover the common periodisation
              percentages cleanly). */}
          <div className="grid grid-cols-3 gap-2 flex-1 content-start">
            {(["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map(
              (d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    haptics.light();
                    setOverlayValue((v) => v + d);
                  }}
                  className="h-12 rounded-xl bg-surface-2 hover:bg-surface-3 active:bg-surface-3 active:scale-95 text-fg text-xl font-semibold tabular-nums transition-all duration-75"
                >
                  {d}
                </button>
              ),
            )}
            {overlayMode === "percent" ? (
              <div aria-hidden />
            ) : (
              <button
                type="button"
                onClick={() => {
                  haptics.light();
                  setOverlayValue((v) =>
                    v.includes(".") || v === "" ? v : v + ".",
                  );
                }}
                aria-label="Decimal point"
                className="h-12 rounded-xl bg-surface-2 hover:bg-surface-3 active:bg-surface-3 active:scale-95 text-fg text-xl font-semibold tabular-nums transition-all duration-75"
              >
                .
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                haptics.light();
                setOverlayValue((v) => v + "0");
              }}
              className="h-12 rounded-xl bg-surface-2 hover:bg-surface-3 active:bg-surface-3 active:scale-95 text-fg text-xl font-semibold tabular-nums transition-all duration-75"
            >
              0
            </button>
            <button
              type="button"
              onClick={() => {
                haptics.light();
                setOverlayValue((v) => v.slice(0, -1));
              }}
              aria-label="Backspace"
              className="h-12 rounded-xl bg-surface-2 hover:bg-surface-3 active:bg-surface-3 active:scale-95 text-fg text-lg font-semibold transition-all duration-75 flex items-center justify-center"
            >
              ⌫
            </button>
          </div>

          {/* Cancel + Apply row — anchored to the bottom via the
              flex-1 digit pad above pushing this down. Apply is
              bigger (flex-[2]) + brand-fill = primary commit. */}
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={closeOverlay}
              className="flex-1 h-10 rounded-xl border border-border hover:border-fg-dim text-fg-muted hover:text-fg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyOverlay}
              disabled={!overlayValue}
              className="flex-[2] h-10 rounded-xl bg-brand hover:bg-brand-strong active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-on-brand text-sm font-semibold transition-all"
            >
              ✓ Apply
            </button>
          </div>
        </div>
      )}
      {/* Header — [<] [TARGET? · TOTAL] [>]
          v9.236 — both chevrons wired into the host's panel-cycle
          loop. LEFT cycles backward, RIGHT cycles forward (was an
          invisible placeholder).
          v9.261 — TARGET/TOTAL tap now opens the inline overlay
          (above) instead of bouncing to the keypad panel. The
          chevrons still cycle panels if the lifter wants the
          full numeric keypad. */}
      <div className="mx-3 mt-2 flex items-stretch gap-2">
        {/* KEYPAD_MODERN — the named way back, matching the numpad's own
            "Plates" button. Same shape, same corner, opposite direction: the
            two panels now point at each other by name. */}
        <button
          type="button"
          onClick={onBackToKeypad}
          aria-label="Back to numpad"
          title="Numpad"
          className="shrink-0 self-center h-8 px-2.5 rounded-lg bg-surface-2 border border-border hover:bg-surface-3 active:bg-surface-3 active:scale-95 text-fg-muted hover:text-fg flex items-center gap-1.5 transition-all duration-75"
        >
          <span aria-hidden className="text-base leading-none">‹</span>
          <span className="text-[11px] font-semibold uppercase tracking-wider">
            Numpad
          </span>
        </button>
        {/* v9.259 — min-w-0 + overflow-hidden so the readout area
            can SHRINK below its natural content width when totals
            grow large (e.g. 220.46 KG·LB at higher loads). Without
            this, a wide TOTAL pushed the X + right-chevron off the
            visible edge — the buttons are shrink-0 so they can't
            give up width on their own. Combined with the TOTAL's
            internal min-w-0 below, the number now degrades to its
            own width budget instead of overflowing the panel. */}
        <div className="flex-1 min-w-0 overflow-hidden flex items-center justify-end gap-3 px-3 py-1">
          {/* v9.238 → v9.256 — "PLATES" mode label retired. The
              barbell SVG + plate grid below already make the
              panel identity unambiguous; the small chrome label
              was duplicating the visual signal. Calc + numpad
              keep their text labels because their panels look
              like generic digit grids without one — plate calc
              has the barbell visualisation doing the same job
              for free. The Target button (when present) now
              floats to the left via its own `mr-auto`. */}
          {hasTarget && (
            // v9.218 — TARGET is a tappable button. Returns to
            // keypad so the lifter can edit the value via the
            // digit grid.
            // v9.219 — text bumped from text-sm to text-lg
            // (matches the "hero number" treatment of the TOTAL
            // readout) so target reads as a peer of total, not a
            // demoted label. Stays text-fg-muted (one tier dimmer
            // than TOTAL) so the visual hierarchy still puts the
            // output (TOTAL) ahead of the input (TARGET).
            // v9.256 — target now shows in `displayUnit` (matches
            // the TOTAL's unit) so the two readouts stay in sync
            // when the user has flipped the conversion.
            <button
              type="button"
              onClick={() => openOverlay("edit")}
              aria-label={`Target ${targetDisplay} ${displayUnitLabel} - tap to edit`}
              title="Tap to edit target"
              className="flex flex-col items-start leading-none mr-auto text-left hover:bg-surface-2 active:bg-surface-2 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
            >
              <span className="text-fg-dim text-[10px] uppercase tracking-wider font-medium">
                Target
              </span>
              <span className="text-fg-muted text-base font-semibold tabular-nums mt-0.5 tracking-tight">
                {targetDisplay} {displayUnitLabel}
              </span>
            </button>
          )}
          {/* v9.256 → v9.257 → v9.258 — TOTAL block: tap the number
              to edit (returns to keypad). The KG·LB chips inline
              next to the number ARE the unit toggle (smaller hit
              targets, stopPropagation so they don't bubble up to
              the outer edit button). Active unit = brand-orange +
              text-sm, inactive = fg-dim + text-[10px]. Two
              affordances in one block, distinct hit zones.
              v9.257 briefly moved the chips to a freed BAR-cell
              slot; v9.258 returns them here because the BAR cell
              is reserved for an upcoming feature. */}
          <div className="relative flex flex-col items-end leading-none">
            <button
              type="button"
              onClick={() => openOverlay("edit")}
              aria-label={`Total ${totalDisplay} ${displayUnitLabel} - tap to edit`}
              title="Tap to edit target"
              className="flex flex-col items-end leading-none hover:bg-surface-2 active:bg-surface-2 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
            >
              <span className="text-fg-dim text-[10px] uppercase tracking-wider font-medium">
                Total
              </span>
              <span
                className={
                  // v9.220 — text-3xl → text-2xl. Matches the calc
                  // + keypad readout sizes — single readout
                  // vocabulary across all three panels.
                  "font-semibold tabular-nums tracking-tight text-2xl mt-0.5 inline-flex items-baseline " +
                  (totalIsEmpty
                    ? "text-fg-dim opacity-60"
                    : hasTarget && targetDiff === 0
                      ? "text-brand"
                      : "text-fg")
                }
              >
                {totalDisplay}
                {/* Spacer so the absolutely-positioned KG·LB
                    cluster below doesn't overlap the number. Fixed width
                    now that the switch is a fixed-width segmented control
                    rather than two words whose size changed with the
                    selection. */}
                <span
                  aria-hidden
                  className="ml-1.5 inline-block w-[4.25rem]"
                />
              </span>
            </button>
            {/* KG·LB toggle — absolute-positioned over the spacer
                above so it sits visually inline with the TOTAL
                number, but is OUTSIDE the parent edit-button so
                tapping it doesn't trigger keypad navigation.
                stopPropagation is belt-and-braces (we're a sibling
                of the button, not a child, so events wouldn't
                bubble through anyway) but reads clearly. */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleDisplayUnit();
              }}
              aria-label={`Display unit: ${displayUnitLabel}. Tap to switch to ${displayUnit === "kg" ? "LB" : "KG"}.`}
              title={`Tap to show total in ${displayUnit === "kg" ? "LB" : "KG"}`}
              // KEYPAD_MODERN — matches the numpad readout's unit switch:
              // equal-sized chips on a grey track, the active one raised.
              // Same control, same look, both panels.
              className="absolute right-0 bottom-0 mb-[2px] inline-flex items-center gap-0.5 rounded-lg bg-surface-3/60 p-0.5 transition-colors"
            >
              {(["kg", "lb"] as const).map((u) => (
                <span
                  key={u}
                  className={
                    "inline-block min-w-[1.75rem] text-center text-[11px] leading-5 rounded-md tracking-wider uppercase font-semibold transition-colors " +
                    (displayUnit === u
                      ? "bg-surface text-brand shadow-sm"
                      : "text-fg-dim")
                  }
                >
                  {u}
                </span>
              ))}
            </button>
          </div>
        </div>
        {/* v9.260 — X (reset-to-auto-decomposed) button removed.
            Beta feedback: "X isn't required on a plate calc?" The
            actions it covered are reachable elsewhere:
              - Reset a SPECIFIC plate → long-press the plate cell
              - Reset ALL → just re-enter the target weight (auto-
                decomposes on type-back)
              - Start fresh → close the panel + re-open
            Removing the X frees ~40px of header width (button +
            gap), which lets the readout grow without clipping
            at heavy weights — the exact concern from the prior
            iteration's "readout clips" feedback. */}
        {/* KEYPAD_MODERN — the right-hand chevron is gone. It cycled forward
            through a loop that has had only two panels since v9.265, so it
            landed on the same numpad the left arrow did. Its width goes to
            the TARGET/TOTAL readout, which is where the clipping complaints
            at heavy loads were coming from. */}
      </div>

      {/* Sub-header — helper hint (left) + diff readout (right when
          target ≠ total). The BAR chip moved out of here in v9.218
          into the 8th grid cell below, freeing this row for just
          the contextual hint + divergence indicator.
          v9.221.1 — restored `mt-1` (was removed entirely in
          v9.221, which over-trimmed and made plate calc SHORTER
          than the keypad). Sub-header now has a 4px top margin
          to breathe from the header above; no bottom margin
          since the grid's p-2 provides separation from below. */}
      {/* v9.246 — sub-header now carries only the diff readout
          (over/under target indicator). Pre-v9.246 it also held
          a "Per side × 2 + bar" prose hint, but the barbell
          visualisation below conveys that spatially — the prose
          was redundant. When target == total (or no target), this
          row is empty space. */}
      {hasTarget && targetDiff !== 0 && (
        <div className="mx-3 mt-1 flex items-center text-[11px]">
          <span
            className={
              "ml-auto text-[10px] uppercase tracking-wider font-medium tabular-nums " +
              (targetDiff > 0 ? "text-warn" : "text-fg-muted")
            }
            title={`${targetDiff > 0 ? "Over" : "Under"} target by ${Math.abs(targetDiff)} ${unitLabel}`}
          >
            {targetDiff > 0 ? "+" : ""}
            {targetDiff} {unitLabel}
          </span>
        </div>
      )}

      {/* v9.245 — Barbell visualisation above the plate grid.
          Shows plates loaded on each side of a horizontal bar.
          Plates auto-sort biggest-to-smallest from inside outward
          (same way real lifters load a bar — heaviest plates
          closest to the sleeve collar). Updates live as plates
          are added/removed. Makes "per side × 2 + bar" visible
          spatially instead of mental math. */}
      <BarbellVisualisation
        perSidePlates={perSidePlates}
        bar={bar}
        unit={unit}
        availableBars={BAR_WEIGHTS[unit]}
        onSelectBar={handleSelectBar}
      />

      {/* Plate grid — 2×4. 7 plate denominations + 1 BAR cell in
          the bottom-right slot. h-[72px] cells matching the calc's
          slim grid. v9.218: inactive cells dimmed further (opacity
          + desaturated text) so the brand-fill active cells command
          the lifter's attention at a glance. */}
      <div className="grid grid-cols-4 gap-1 p-2">
        {denoms.map((denom, idx) => {
          const count = counts[idx] ?? 0;
          const active = count > 0;
          return (
            <div key={denom} className="relative">
              <button
                type="button"
                onPointerDown={() => handlePlatePointerDown(idx)}
                onPointerUp={(e) => handlePlatePointerUp(idx, e)}
                onPointerCancel={handlePlatePointerCancel}
                onPointerLeave={handlePlatePointerCancel}
                aria-label={`${denom} ${unitLabel} plate per side, currently ${count}. Tap to add, long-press to clear.`}
                // v9.220 — `pr-2.5` when active so the centred
                // content shifts ~5px LEFT to clear the corner
                // minus button. Without this, longer denom labels
                // ("1.25") read flush against the minus button
                // and felt visually cramped. The 2-char denoms
                // (25, 20, 15, 10) shift slightly too — barely
                // noticeable, and it makes the active-state
                // geometry consistent regardless of denom length.
                //
                // v9.245.4 — active state now uses the plate's
                // real-world colour as the cell background
                // (red 25, blue 20, yellow 15, green 10, white 5,
                // grey small plates) instead of the brand-orange
                // uniform fill. Matches the SVG barbell + colour-
                // dot mnemonic for full visual consistency: every
                // representation of a 25 KG plate (cell fill, SVG
                // plate, corner dot) shows red. Light plates
                // (white 5, yellow 15) get dark text for
                // contrast; dark plates get white text. Falls
                // back to brand-orange for any plate denom not in
                // the colour map.
                className={
                  "w-full h-[72px] rounded-xl active:scale-95 select-none flex flex-col items-center justify-center transition-all duration-75 " +
                  (active
                    ? "shadow-sm pr-2.5"
                    : "bg-surface-2/40 hover:bg-surface-2 text-fg-muted")
                }
                style={
                  active
                    ? plateActiveStyle(plateColours[denom])
                    : undefined
                }
              >
                <span
                  className={
                    "text-base font-semibold tabular-nums leading-none " +
                    (active ? "" : "text-fg-muted/80")
                  }
                  style={active ? { color: "inherit" } : undefined}
                >
                  {denom}
                </span>
                <span
                  className={
                    "text-[10px] uppercase tracking-wider mt-1 leading-none " +
                    (active ? "opacity-80" : "text-fg-dim/70")
                  }
                  style={active ? { color: "inherit" } : undefined}
                >
                  {unitLabel}
                </span>
                {active && (
                  <span
                    className="text-[11px] font-bold tabular-nums mt-1 leading-none"
                    style={{ color: "inherit" }}
                  >
                    × {count}
                  </span>
                )}
              </button>
              {active && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    decrementPlate(idx);
                  }}
                  // stopPropagation on the button below catches the
                  // click, but pointerdown on the parent cell fires
                  // first and starts the long-press timer. Cancel
                  // here so the corner − tap doesn't accidentally
                  // arm a reset.
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    handlePlatePointerCancel();
                  }}
                  aria-label={`Remove one ${denom} ${unitLabel} plate per side`}
                  // v9.219 → v9.220 — sized at h-6 w-6 (24 px).
                  // h-5 (20) was below comfortable touch size;
                  // h-7 (28) read as too dominant on the cell.
                  // h-6 lands in the goldilocks zone — visible
                  // tap target without crowding the central
                  // weight + count content.
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-bg/30 hover:bg-bg/50 text-on-brand flex items-center justify-center text-sm leading-none font-bold transition-colors"
                >
                  −
                </button>
              )}
            </div>
          );
        })}
        {/* v9.261 → v9.265.5 — CALC button. Opens the inline edit
            overlay defaulting to TOTAL ("edit") mode; lifter can
            switch to PERCENT inside via the mode toggle if they
            want periodisation math. Distinct visual treatment
            (border-brand outline rather than fill) so it reads
            as a CALC affordance, different from the plate cells
            around it.
            Label evolution:
              v9.261: "%" with "of target" subtitle
              v9.264: "%" with "CALC" subtitle (TOTAL-first via
                      TARGET-button affordance; PERCENT-default
                      here)
              v9.265.5: "CALC" primary with "%" subtitle, AND
                      defaulting to TOTAL mode on open. Beta
                      feedback: "should we change this to CALC/%
                      and load it on the TOTAL selector first?" —
                      TOTAL is the more common entry path
                      (lifters typing a fresh target weight),
                      PERCENT is the deeper feature behind the
                      mode toggle. Promoting CALC as the primary
                      label matches that priority. */}
        <button
          type="button"
          onClick={() => openOverlay("edit")}
          aria-label={`Open calculator to edit target weight or apply a percentage (current target ${targetDisplay} ${unitLabel})`}
          title="Calculator (edit target / percentage)"
          className="w-full h-[72px] rounded-xl border border-brand/60 bg-surface-2/40 hover:bg-surface-2 hover:border-brand active:scale-95 select-none flex flex-col items-center justify-center gap-1 text-brand transition-all duration-75"
        >
          <span className="text-base font-semibold leading-none uppercase tracking-wider">
            CALC
          </span>
          {/* v9.265.6 — % bumped from text-[10px] → text-sm so the
              percentage-calc affordance reads clearly without
              overpowering the CALC label above. Per beta
              feedback. */}
          <span className="text-sm font-semibold leading-none opacity-80">
            %
          </span>
        </button>
      </div>

      {/* Apply bar — v9.220.1: `mb-3` → `mb-2` to match the
          keypad's Done bar exactly (was 12px bottom margin, now
          8px). The extra 4px below the apply button made the
          plate calc panel sit 4px taller than the keypad.
          v9.237 — `mt-auto` added. Plate calc is the shortest
          natural-height panel (~326 vs 336 for keypad/calc). The
          shared min-h-[340px] floor on the parent + this mt-auto
          push send the Apply button to the same y-position the
          other panels' commit buttons land at, so cycling doesn't
          jolt the primary affordance up or down. The ~14px of
          extra space falls above this row (between the per-side
          breakdown and Apply) where it reads as natural breathing
          room rather than a missing-element gap. */}
      {/* v9.265 — bottom action row split into Cancel + Apply.
          Cancel calls the outer `onClose` (dismisses the entire
          keypad, not just the panel) — matches the plate-calc
          inline overlay's Cancel + Apply pattern that the lifter
          is already familiar with. Apply stays brand-fill +
          flex-[2] (the primary commit affordance), Cancel is the
          slimmer secondary affordance on the left. */}
      <div className="mt-auto mx-3 mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel and close keypad"
          className="flex-1 h-11 rounded-xl border border-border hover:border-fg-dim text-fg-muted hover:text-fg text-sm font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={totalIsEmpty}
          aria-label={`Apply ${totalDisplay} ${unitLabel} to keypad`}
          className={
            "flex-[2] h-11 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors " +
            (totalIsEmpty
              ? "bg-surface-2 text-fg-dim cursor-not-allowed"
              : "bg-brand hover:bg-brand-strong active:bg-brand-strong text-on-brand")
          }
        >
          <span aria-hidden className="text-lg leading-none">✓</span>
          <span className="text-base">Apply {totalDisplay} {unitLabel}</span>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * BarbellVisualisation
 *
 * Horizontal-bar SVG illustration showing plates loaded on each
 * side. The visual makes "per side × 2 + bar" concrete — what is
 * otherwise mental math becomes "I can SEE the plates on each side
 * of the bar." (v9.245 originally shipped as "Variant A" in the
 * admin-preview comparison; promoted to default in v9.246.)
 *
 * Layout:
 *   [plates on left] [bar sleeve] ━━━━━━━━━━ [bar sleeve] [plates on right]
 *
 * Plates auto-sort biggest-to-smallest from inside-out (the way
 * real lifters load a bar — heaviest plates against the collar).
 * Plate HEIGHT scales linearly with denomination so a 25 KG plate
 * visibly towers over a 1.25 KG; plate WIDTH is uniform so heavy
 * stacks don't overflow the viewbox.
 * ─────────────────────────────────────────────────────────────── */

export function BarbellVisualisation({
  perSidePlates,
  bar,
  unit,
  availableBars,
  onSelectBar,
}: {
  perSidePlates: Array<{ denom: number; count: number }>;
  bar: number;
  unit: WeightUnit;
  /** v9.257 — list of selectable bar weights (BAR_WEIGHTS[unit]).
   *  Passed in rather than read here so the parent owns the bar
   *  cycle list — keeps the visualisation a pure render concern. */
  availableBars: readonly number[];
  /** v9.257 — called when the user taps a bar option in the centre
   *  stack. Replaces the old "tap separate BAR cell to cycle" path;
   *  now the user picks the exact bar weight they want from a
   *  visible list inside the barbell SVG's centre. */
  onSelectBar: (next: number) => void;
}) {
  // Flatten per-side counts into an ordered stack: biggest plates
  // first (closest to sleeve). Mirrored on the other side.
  const stack: number[] = [];
  for (const { denom, count } of perSidePlates) {
    for (let i = 0; i < count; i++) stack.push(denom);
  }
  stack.sort((a, b) => b - a);

  // Per-side total weight — sum of plate denoms (one side's worth).
  const perSideTotal = stack.reduce((sum, denom) => sum + denom, 0);
  const unitLabel = unit.toUpperCase();
  const plateColours =
    unit === "kg" ? PLATE_COLOURS_KG : PLATE_COLOURS_LB;

  // SVG geometry — v9.245.4 plate widths now scale with
  // denomination (was uniform PLATE_WIDTH=9). Heavier plates
  // render visibly thicker than smaller plates, matching the
  // real-world hardware where a 25 KG plate is notably thicker
  // than a 1.25 KG change plate. Width ramps linearly from
  // ~8px (smallest fraction) to ~22px (largest standard plate).
  // Heights ramp the same way (already did pre-v9.245.4).
  // v9.245.6 — SVG height reverted from 145 → 120 px now that
  // labels live in HTML above the SVG (no in-SVG label band to
  // reserve for). cy back to SVG_H/2 for vertical centring.
  const BAR_LEN = 200;
  const SLEEVE_LEN = 22;
  const SVG_H = 120;
  const cy = SVG_H / 2;
  const maxDenom = unit === "kg" ? 25 : 45;
  const plateH = (denom: number) => {
    const ratio = Math.min(1, denom / maxDenom);
    return 36 + ratio * 50; // 36-86 height range
  };
  // v9.245.4 — stepped plate widths. Width grows with
  // denomination so 25 KG plates look chunky, 1.25 KG plates
  // look thin (matches real-world hardware proportions).
  const plateW = (denom: number) => {
    const ratio = Math.min(1, denom / maxDenom);
    return 8 + ratio * 14; // 8-22 width range
  };

  // v9.245.4 — pre-compute per-plate x positions + dimensions.
  // With variable widths, each plate's x must accumulate from
  // the cursor inside-out. `leftPlates`/`rightPlates` carry the
  // resolved geometry so the render loop is purely declarative.
  //
  // v9.245.8 → v9.245.10 — width-based cap. Capping by count was
  // the wrong axis: 8 × 25 KG plates occupy the same horizontal
  // real estate as 12 × 10 KG plates, so a flat count cap was
  // either wasting space (heavy denoms only) or hiding too much
  // (small denoms). Now we accumulate plate widths and stop when
  // we'd exceed MAX_STACK_WIDTH = 220 SVG units (= 10 × 22-unit
  // widths of the heaviest plate). Results per denom:
  //   25 KG: 10 visible  (250 KG/side, 520 KG total — past WR)
  //   20 KG: 11 visible  (220 KG/side)
  //   15 KG: 13 visible  (195 KG/side)
  //   10 KG: 16 visible  (160 KG/side)
  //   5  KG: 20 visible  (100 KG/side)
  //   2.5 KG: 23 visible (57.5 KG/side)
  //   1.25 KG: 25 visible (31.25 KG/side)
  //
  // v9.245.10 bumped from 176→220 per user review — the 176 cap
  // (8 × 25 KG = 200 KG/side, 420 KG total + bar) was below
  // strongman / world-record-deadlift territory. 220 covers all
  // realistic loads (Eddie Hall's 500 KG WR was 5 plates per
  // side; 10 plates per side leaves comfortable headroom for
  // 1RM attempts at the top of the sport).
  //
  // SVG aspect ratio worst case: svgW = 700 with both sides at
  // MAX_STACK_WIDTH, aspect = 5.83:1. At iPhone width 350 the
  // barbell renders ~60 px tall — still readable, just slightly
  // tighter than the 176 cap. Trade accepted for the load
  // coverage headroom.
  const MAX_STACK_WIDTH = 220;
  const visibleStack: number[] = [];
  let cumulativeStackWidth = 0;
  for (const denom of stack) {
    const w = plateW(denom);
    if (cumulativeStackWidth + w > MAX_STACK_WIDTH) break;
    visibleStack.push(denom);
    cumulativeStackWidth += w;
  }
  const hiddenPlateCount = stack.length - visibleStack.length;
  const overflowed = hiddenPlateCount > 0;

  const stackWidth = visibleStack.reduce(
    (sum, denom) => sum + plateW(denom),
    0,
  );
  const svgW = stackWidth + SLEEVE_LEN + BAR_LEN + SLEEVE_LEN + stackWidth + 16;
  const leftSleeveX = stackWidth + 8;
  const barX = leftSleeveX + SLEEVE_LEN;
  const rightSleeveX = barX + BAR_LEN;

  type PlateRender = { denom: number; x: number; w: number; h: number };
  const leftPlates: PlateRender[] = [];
  let leftCursor = leftSleeveX;
  for (const denom of visibleStack) {
    const w = plateW(denom);
    leftCursor -= w;
    leftPlates.push({ denom, x: leftCursor, w, h: plateH(denom) });
  }
  const rightPlates: PlateRender[] = [];
  let rightCursor = rightSleeveX + SLEEVE_LEN;
  for (const denom of visibleStack) {
    const w = plateW(denom);
    rightPlates.push({ denom, x: rightCursor, w, h: plateH(denom) });
    rightCursor += w;
  }

  const ariaLabel =
    stack.length === 0
      ? `Empty barbell, ${bar} ${unitLabel}`
      : `Barbell loaded with ${stack.length} plates per side, ${perSideTotal} ${unitLabel} each side`;

  // White-plate stroke fallback — the 5 KG plate fills #ffffff
  // which would disappear against the panel's light surface. Use
  // a slightly darker stroke for the 5 KG specifically.
  const plateStroke = (denom: number) =>
    plateColours[denom] === "#ffffff"
      ? "#9ca3af"
      : // Theme-aware seam between stacked plates — was var(--color-bg)
        // (near-white, low-contrast in light). The border token reads as
        // a hairline separator in both themes.
        "var(--color-border)";

  // v9.245.6 — labels were moved out of the SVG into an HTML row
  // above the SVG. SVG-embedded <text> always scales with the
  // viewBox, so even with inverse fontSize scaling the labels
  // would visibly shrink at heavy loads. HTML text is rendered
  // in fixed CSS pixels, immune to the SVG's zoom level.
  // Tradeoff: HTML labels use flexbox justify-between (edges +
  // centre) which doesn't track per-plate-stack geometry the way
  // SVG <text> did. For loaded bars this still reads well
  // (plate stacks grow toward edges, labels stay at edges); for
  // the empty-bar case the side labels float a touch wide of
  // the centred bar but the centre "bar weight" label still
  // anchors the row. The labelLeftX / labelCentreX / labelRightX
  // SVG-coordinate computations are gone with the SVG <text>
  // elements they were positioning.

  return (
    <div className="mx-3 mt-2">
      {/* v9.245.6 — HTML label row above the SVG. Flex
          justify-between distributes the three labels at
          left edge / centre / right edge of the row, so they
          always render at the same CSS-pixel size regardless of
          how wide the SVG below grows. Per-side labels dim when
          the per-side total is 0 so an empty bar reads as
          "nothing loaded" rather than asserting a zero value.
          Padded `px-1` so the edge labels don't sit flush with
          the container edge — gives visual breathing room
          relative to the bar geometry below. */}
      {/* v9.257 → v9.258 → v9.260 — label row above the SVG.
          Selected bar renders inline with the side totals as a
          single-line centre label. The centre is a BUTTON: tap
          it to cycle to the next bar weight in availableBars
          (wraps at the end). Muted alternative options also sit
          inside the SVG container's bottom strip as an
          absolute-positioned row of tap-to-pick chips.

          Two gestures, two affordances:
            - Tap centre BAR XX KG  → cycle to next (sequential)
            - Tap a muted option    → jump to that bar (specific)

          v9.258 had the centre label as a static span by mistake
          — beta testers reported "tapping BAR 20 KG doesn't
          switch between weights." Promoted to a button now. */}
      <div className="flex items-center justify-between px-1 mb-1 text-sm font-semibold tabular-nums">
        <span
          className={
            perSideTotal === 0 ? "text-fg-dim opacity-60" : "text-fg"
          }
        >
          {perSideTotal} {unitLabel}
        </span>
        <button
          type="button"
          onClick={() => {
            // Cycle to next bar in availableBars, wrap at end.
            const idx = availableBars.indexOf(bar);
            const next = availableBars[(idx + 1) % availableBars.length];
            if (next !== undefined) onSelectBar(next);
          }}
          aria-label={`Bar weight ${bar} ${unitLabel}. Tap to cycle.`}
          title="Tap to cycle bar weight"
          className="text-fg px-1.5 py-0.5 -mx-1.5 rounded hover:bg-surface-2 active:bg-surface-2 transition-colors"
        >
          BAR {bar} {unitLabel}
        </button>
        <span
          className={
            perSideTotal === 0 ? "text-fg-dim opacity-60" : "text-fg"
          }
        >
          {perSideTotal} {unitLabel}
        </span>
      </div>
      {/* v9.258 — relative wrapper so the muted-bar-options
          overlay can sit ABSOLUTE inside the SVG container's
          empty bottom region without pushing the SVG height
          around. Pre-fix the options were stacked above the SVG
          (label row) and shifted everything below them. */}
      <div className="relative">
        <svg
          viewBox={`0 0 ${svgW} ${SVG_H}`}
          className="w-full h-auto max-h-[120px]"
          role="img"
          aria-label={ariaLabel}
        >
        {/* Bar shaft */}
        <rect
          x={barX}
          y={cy - 4}
          width={BAR_LEN}
          height={8}
          rx={3}
          fill="var(--color-fg-dim)"
        />
        {/* Left sleeve (thicker collar where plates load) */}
        <rect
          x={leftSleeveX}
          y={cy - 8}
          width={SLEEVE_LEN}
          height={16}
          rx={2}
          fill="var(--color-fg-muted)"
        />
        {/* Right sleeve */}
        <rect
          x={rightSleeveX}
          y={cy - 8}
          width={SLEEVE_LEN}
          height={16}
          rx={2}
          fill="var(--color-fg-muted)"
        />
        {/* Bar end-caps (small nubs to suggest the collar lock) */}
        <rect
          x={leftSleeveX - 5}
          y={cy - 5}
          width={5}
          height={10}
          rx={1}
          fill="var(--color-fg-dim)"
        />
        <rect
          x={rightSleeveX + SLEEVE_LEN}
          y={cy - 5}
          width={5}
          height={10}
          rx={1}
          fill="var(--color-fg-dim)"
        />
        {/* v9.245.2 — the bar weight label moved to the unified
            label row at the top of the SVG (see <text>
            labelCentreX above). Empty here intentionally; keeps
            the on-shaft area clear so the bar's grey colour
            reads as the bar itself. */}
        {/* Plates on LEFT side — render from pre-computed
            geometry. v9.245.4: variable widths per plate
            (larger denoms render thicker) so heavy stacks
            visibly outweigh small change-plate stacks. Real
            powerlifting plate colours per the IPF/IWF standard
            (see PLATE_COLOURS_* maps at top of file). */}
        {leftPlates.map((p, i) => (
          <rect
            key={`L-${i}`}
            x={p.x}
            y={cy - p.h / 2}
            width={p.w - 1}
            height={p.h}
            rx={2}
            fill={plateColours[p.denom] ?? "var(--color-brand)"}
            stroke={plateStroke(p.denom)}
            strokeWidth={0.75}
          />
        ))}
        {rightPlates.map((p, i) => (
          <rect
            key={`R-${i}`}
            x={p.x}
            y={cy - p.h / 2}
            width={p.w - 1}
            height={p.h}
            rx={2}
            fill={plateColours[p.denom] ?? "var(--color-brand)"}
            stroke={plateStroke(p.denom)}
            strokeWidth={0.75}
          />
        ))}

        {/* v9.245.8 — overflow indicator. When the user has more
            plates per side than MAX_VISIBLE_PER_SIDE, render three
            dots at the outer edge of each plate stack to signal
            "there are more plates than shown." The HTML per-side
            label above shows the correct total so the user has
            the truth; this is just a visual cue that the bar art
            is approximate. Plate count beyond 8 per side is rare
            (well past competition record loads) so this
            indicator is a stress-test safety net, not a routine
            sight. */}
        {overflowed && (
          <>
            <text
              x={leftCursor - 4}
              y={cy}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="22"
              fontWeight="700"
              fill="var(--color-fg-dim)"
              letterSpacing={1}
            >
              ···
            </text>
            <text
              x={rightCursor + 4}
              y={cy}
              textAnchor="start"
              dominantBaseline="middle"
              fontSize="22"
              fontWeight="700"
              fill="var(--color-fg-dim)"
              letterSpacing={1}
            >
              ···
            </text>
          </>
        )}
        </svg>
        {/* v9.258 — muted bar-weight alternatives, absolute-
            positioned inside the SVG container's empty bottom
            strip. The barbell SVG has ~40px of empty space below
            the bar shaft (the bar lives at the SVG's vertical
            centre; plates extend up + down from there but only
            on the OUTSIDES of the sleeves). This row sits in the
            centre column of that empty strip, well clear of any
            loaded plates which sit far from the centre.
            Each option is tap-to-select; the selected bar is
            omitted (shown in the label row above). availableBars
            is filtered defensively even though we expect the
            selected one to always be in the cycle list. */}
        {availableBars.length > 1 && (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-1 flex items-center gap-2 pointer-events-none"
            role="group"
            aria-label="Other bar weights"
          >
            {availableBars
              .filter((b) => b !== bar)
              .map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => onSelectBar(b)}
                  aria-label={`Set bar weight to ${b} ${unitLabel}`}
                  className="pointer-events-auto text-fg-dim hover:text-fg-muted active:text-fg text-[11px] font-medium tabular-nums uppercase tracking-wide px-1.5 py-0.5 rounded hover:bg-surface-2 active:bg-surface-2 transition-colors"
                >
                  {b} {unitLabel}
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
